import React from 'react';
import { useTranslation } from 'react-i18next';
import { generateEpisodeJSONLD } from '@jwp/ott-common/src/utils/structuredData';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useTranslationKey } from '@jwp/ott-hooks-react/src/useTranslationKey';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { createVideoMetadata } from '@jwp/ott-common/src/utils/metadata';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useSeriesData from '@jwp/ott-hooks-react/src/useSeriesData';
import useAutoFocus from '@jwp/ott-ui-react/src/hooks/useAutoFocus';
import { useAutoScroll } from '@jwp/ott-ui-react/src/hooks/useAutoScroll';
import useSeriesParams from '@jwp/ott-ui-react/src/hooks/useSeriesParams';
import useSeriesHandlers from '@jwp/ott-ui-react/src/hooks/useSeriesHandlers';
import useTrailerData from '@jwp/ott-hooks-react/src/useTrailerData';
import type { ScreenComponent } from '@jwp/ott-ui-react/types/screens';
import Cinema from '@jwp/ott-ui-react/src/containers/Cinema/Cinema';
import TrailerModal from '@jwp/ott-ui-react/src/containers/TrailerModal/TrailerModal';
import ShareButton from '@jwp/ott-ui-react/src/components/ShareButton/ShareButton';
import FavoriteButton from '@jwp/ott-ui-react/src/containers/FavoriteButton/FavoriteButton';
import VideoMetaData from '@jwp/ott-ui-react/src/components/VideoMetaData/VideoMetaData';
import VideoDefinitionList from '@jwp/ott-ui-react/src/components/VideoDefintionsList/VideoDefinitionList';
import MediaHead from '@jwp/ott-ui-react/src/components/MediaHead/MediaHead';
import MediaHeroInfo from '@jwp/ott-ui-react/src/components/MediaHeroInfo/MediaHeroInfo';
import MediaHeroButtons from '@jwp/ott-ui-react/src/components/MediaHeroButtons/MediaHeroButtons';
import MediaHero from '@jwp/ott-ui-react/src/components/MediaHero/MediaHero';
import MediaTabs from '@jwp/ott-ui-react/src/components/MediaTabs/MediaTabs';
import MediaButtonTrailer from '@jwp/ott-ui-react/src/components/MediaButtonTrailer/MediaButtonTrailer';
import SeasonSelector from '@jwp/ott-ui-react/src/components/SeasonSelector/SeasonSelector';
import MarkdownComponent from '@jwp/ott-ui-react/src/components/MarkdownComponent/MarkdownComponent';
import MediaTabAbout from '@jwp/ott-ui-react/src/components/MediaTabAbout/MediaTabAbout';
import StartWatchingButton from '@jwp/ott-ui-react/src/containers/StartWatchingButton/StartWatchingButton';
import ErrorPage from '@jwp/ott-ui-react/src/components/ErrorPage/ErrorPage';
import { formatAgeRating, formatSeriesMetaString } from '@jwp/ott-common/src/utils/formatting';
import CardGrid from '@jwp/ott-ui-react/src/components/CardGrid/CardGrid';
import Fade from '@jwp/ott-ui-react/src/components/Animation/Fade/Fade';
import EpisodeList from '@jwp/ott-ui-react/src/components/EpisodeList/EpisodeList';
import Loading from '@jwp/ott-ui-react/src/pages/Loading/Loading';
import VideoTags from '@jwp/ott-ui-react/src/components/VideoTags/VideoTags';
import Link from '@jwp/ott-ui-react/src/components/Link/Link';
import styles from '@jwp/ott-ui-react/src/pages/ScreenRouting/mediaScreens/MediaMovie/MediaMovie.module.scss';
import env from '@jwp/ott-common/src/env';

import MediaDescription from '../../../../components/MediaDescription/MediaDescription';

const MediaReadingSeries: ScreenComponent<PlaylistItem> = ({ data }) => {
  const { t } = useTranslation('video');
  const translationKey = useTranslationKey('title');
  const breakpoint = useBreakpoint();
  const { seriesId, play, feedId, episodeId: episodeIdParam, searchParams, setSearchParams } = useSeriesParams();

  const {
    series,
    episode,
    episodeMetadata,
    initialEpisodeId,
    isFirstEpisode,
    filters,
    season,
    episodesPlaylist,
    recommendations,
    accessModel,
    nextItem,
    seriesMetadata,
    isRecommendationsLoading,
    isLoading,
    isEpisodesLoading,
    isEpisodesFetching,
    isEntitled,
    areEpisodesEntitled,
    isFavoritesEnabled,
    isSharingEnabled,
    isLoggedIn,
    hasSubscription,
    hasRecommendations,
    watchHistory,
    hasNextEpisodesPage,
    fetchNextEpisodes,
    getCardSeriesUrl,
    setActiveSeason,
    resolveEpisode,
  } = useSeriesData(data, seriesId, episodeIdParam);
  const { handlePlay, handlePlayEpisode, handleBack, handleComplete } = useSeriesHandlers(
    setSearchParams,
    searchParams,
    nextItem,
    resolveEpisode,
    areEpisodesEntitled,
    feedId,
  );
  const { isTrailerPlaying, hasTrailer, trailerItem, trailerTitle, closeTrailer, playTrailer } = useTrailerData(data);

  const structuredData = series && generateEpisodeJSONLD(series, data, env.APP_PUBLIC_URL, episode, episodeMetadata);
  const canonicalUrl = data ? `${env.APP_PUBLIC_URL}${mediaURL({ id: data.mediaid, title: data.title })}` : window.location.href;
  const recommendationsTabTitle = hasRecommendations ? (recommendations?.[translationKey] as string) || recommendations?.title || '' : '';

  const smallScreen = breakpoint < Breakpoint.md;

  const customMetadata = [
    ...createVideoMetadata(
      { ...data, pubdate: 0, rating: '' },
      {
        ...seriesMetadata,
        hoursAbbreviation: t('common:abbreviation.hours'),
        minutesAbbreviation: t('common:abbreviation.minutes'),
      },
    ),
    ...(data?.reader ? [data.reader as string] : []),
  ];

  const readerTags = [data?.audioLanguages, formatAgeRating(data?.rating)].filter(Boolean);

  const secondaryMetadata = episodeMetadata && episode && (
    <>
      <strong>{formatSeriesMetaString(episodeMetadata.seasonNumber, episodeMetadata.episodeNumber)}</strong> - {episode.title}
    </>
  );

  const purchaseLink =
    episode?.purchase_link_url && episode?.purchase_link_display ? (
      <Link href={episode.purchase_link_url as string} target="_blank">
        {episode.purchase_link_display as string}
      </Link>
    ) : (
      ''
    );

  useAutoFocus(episode?.mediaid);
  useAutoScroll(episode?.mediaid); // scroll when the episode changes

  if (isLoading) return <Loading />;

  if (!data || !series || !episode) return <ErrorPage title={t('series_error')} />;

  return (
    <React.Fragment>
      <MediaHead canonicalUrl={canonicalUrl} data={data} structuredData={structuredData} />
      <MediaHero image={data.backgroundImage}>
        <MediaHeroInfo
          title={data.title}
          subtitle={initialEpisodeId ? secondaryMetadata : undefined}
          description={<MediaDescription item={initialEpisodeId && episode ? episode : data} />}
          metadataComponent={<VideoMetaData attributes={customMetadata} />}
          tagsComponent={<VideoTags attributes={readerTags} />}
          definitionComponent={
            <VideoDefinitionList
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.author'), value: data?.author as string },
                { label: t('video:metadata.illustrator'), value: data?.illustrator as string },
                { label: t('video:metadata.publisher'), value: data?.publisher as string },
              ]}
            />
          }
        />
        <MediaHeroButtons
          cta={
            <>
              <StartWatchingButton
                key={seriesId} // necessary to fix autofocus on TalkBack
                item={episode}
                episodeMetadata={episodeMetadata}
                isSequentialEpisode={!isFirstEpisode}
                onClick={handlePlay}
              />
              {!smallScreen && (
                <MediaButtonTrailer hasTrailer={hasTrailer} trailerItem={trailerItem} isTrailerPlaying={isTrailerPlaying} onClick={playTrailer} />
              )}
            </>
          }
        >
          {smallScreen && <MediaButtonTrailer hasTrailer={hasTrailer} trailerItem={trailerItem} isTrailerPlaying={isTrailerPlaying} onClick={playTrailer} />}
          {isFavoritesEnabled ? <FavoriteButton item={data} /> : undefined}
          {isSharingEnabled ? <ShareButton title={data.title} description={data.description} url={canonicalUrl} /> : undefined}
        </MediaHeroButtons>
      </MediaHero>
      <MediaTabs
        titles={[t('video:episodes'), recommendationsTabTitle, t('video:about_tab')]}
        isLoading={isLoading || isEpisodesLoading || isRecommendationsLoading}
      >
        {episodesPlaylist && (
          <Fade open={!isEpisodesFetching} duration={500}>
            {filters.length > 1 && (
              <SeasonSelector
                isLoading={isEpisodesFetching}
                value={season.value}
                year={season.year}
                description={season.description}
                options={filters}
                onChange={(value) => setActiveSeason(value)}
              />
            )}
            <EpisodeList
              playlist={episodesPlaylist}
              areEpisodesEntitled={areEpisodesEntitled}
              isLoading={isLoading || isEpisodesFetching}
              hasMore={hasNextEpisodesPage}
              loadMore={fetchNextEpisodes}
              watchHistory={watchHistory}
              onEpisodeClick={handlePlayEpisode}
              renderEpisodeMetadata={(item) => (
                <>
                  <VideoTags attributes={[item?.audioLanguages, formatAgeRating(item?.rating)]} />
                  {item.reader ? <VideoMetaData attributes={[item.reader].filter(Boolean) as string[]} /> : null}
                </>
              )}
            />
          </Fade>
        )}
        {recommendations && (
          <CardGrid
            playlist={recommendations}
            isLoading={isLoading || isRecommendationsLoading}
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            hasSubscription={hasSubscription}
            getUrl={getCardSeriesUrl}
          />
        )}
        <MediaTabAbout description={<MarkdownComponent className={styles.markdown} markdownString={data.description} headerOffset={1} />}>
          <div className={styles.aboutDefinitionList}>
            <VideoDefinitionList
              className={styles.definitionList}
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.age'), value: formatAgeRating(data?.rating as string) },
                { label: t('video:metadata.audio_languages'), value: data?.audioLanguages as string },
                { label: t('video:metadata.reader'), value: data?.reader as string },
              ]}
            />
            <VideoDefinitionList
              className={styles.definitionList}
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.author'), value: data?.author as string },
                { label: t('video:metadata.illustrator'), value: data?.illustrator as string },
                { label: t('video:metadata.publisher'), value: data?.publisher as string },
                { label: t('video:metadata.purchase_link'), value: purchaseLink, hiddenLabel: true },
              ]}
            />
          </div>
        </MediaTabAbout>
      </MediaTabs>
      <Cinema
        open={!isLoading && play && isEntitled}
        onClose={handleBack}
        item={episode}
        seriesItem={data}
        title={data.title}
        primaryMetadata={
          <>
            <VideoTags attributes={readerTags} />
            <VideoMetaData attributes={customMetadata} />
          </>
        }
        secondaryMetadata={secondaryMetadata}
        onComplete={handleComplete}
        feedId={feedId ?? undefined}
      />
      <TrailerModal item={trailerItem} title={trailerTitle} open={isTrailerPlaying} onClose={closeTrailer} />
    </React.Fragment>
  );
};

export default MediaReadingSeries;
